import { useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { batchReviewClassifications } from "../api/requests/review.requests";
import { QUERY_KEYS } from "../api/config";
import type { BatchReviewItem } from "../types/review";

const CHUNK_SIZE = 100;

export type ChunkStatus = "pending" | "in_progress" | "success" | "failed";

export interface ChunkState {
  index: number;
  count: number;
  status: ChunkStatus;
  error?: string;
  imageId?: number;
}

interface UseBatchChunkedReviewResult {
  chunks: ChunkState[];
  isRunning: boolean;
  execute: (items: BatchReviewItem[]) => Promise<boolean>;
  retryFromFailed: () => Promise<boolean>;
  reset: () => void;
}

export const useBatchChunkedReview = (): UseBatchChunkedReviewResult => {
  const { t } = useTranslation("mlExports");
  const queryClient = useQueryClient();
  const [chunks, setChunks] = useState<ChunkState[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const itemsRef = useRef<BatchReviewItem[]>([]);

  const updateChunk = (index: number, update: Partial<ChunkState>) => {
    setChunks((prev) =>
      prev.map((c) => (c.index === index ? { ...c, ...update } : c)),
    );
  };

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REVIEW_CLASSIFICATIONS] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REVIEW_DETAIL] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REVIEW_ACTIVITY] });
  }, [queryClient]);

  const runChunks = useCallback(
    async (allItems: BatchReviewItem[], startFrom: number): Promise<boolean> => {
      const chunkCount = Math.ceil(allItems.length / CHUNK_SIZE);

      if (startFrom === 0) {
        setChunks(
          Array.from({ length: chunkCount }, (_, i) => ({
            index: i,
            count: Math.min(CHUNK_SIZE, allItems.length - i * CHUNK_SIZE),
            status: "pending" as ChunkStatus,
          })),
        );
      }

      setIsRunning(true);
      let success = true;

      for (let i = startFrom; i < chunkCount; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, allItems.length);
        const chunkItems = allItems.slice(start, end);

        updateChunk(i, { status: "in_progress" });

        try {
          await batchReviewClassifications({ items: chunkItems });
          updateChunk(i, { status: "success" });
        } catch (err: unknown) {
          const apiError = (err as {
            response?: { data?: { error?: {
              detail?: string;
              image_id?: number;
            } } }
          })?.response?.data?.error;

          updateChunk(i, {
            status: "failed",
            error: apiError?.detail ?? t("chunkProgress.chunkFailed"),
            imageId: apiError?.image_id,
          });
          success = false;
          break;
        }
      }

      setIsRunning(false);
      if (success) invalidateAll();
      return success;
    },
    [invalidateAll, t],
  );

  const execute = useCallback(
    async (items: BatchReviewItem[]): Promise<boolean> => {
      itemsRef.current = items;

      if (items.length <= CHUNK_SIZE) {
        setChunks([{ index: 0, count: items.length, status: "in_progress" }]);
        setIsRunning(true);
        try {
          await batchReviewClassifications({ items });
          setChunks([{ index: 0, count: items.length, status: "success" }]);
          invalidateAll();
          return true;
        } catch (err: unknown) {
          const apiError = (err as {
            response?: { data?: { error?: {
              detail?: string;
              image_id?: number;
            } } }
          })?.response?.data?.error;
          setChunks([{
            index: 0,
            count: items.length,
            status: "failed",
            error: apiError?.detail ?? t("chunkProgress.batchFailed"),
            imageId: apiError?.image_id,
          }]);
          return false;
        } finally {
          setIsRunning(false);
        }
      }

      return runChunks(items, 0);
    },
    [runChunks, invalidateAll, t],
  );

  const retryFromFailed = useCallback(async (): Promise<boolean> => {
    const failedIndex = chunks.findIndex((c) => c.status === "failed");
    if (failedIndex === -1) return true;
    return runChunks(itemsRef.current, failedIndex);
  }, [chunks, runChunks]);

  const reset = useCallback(() => {
    setChunks([]);
    setIsRunning(false);
    itemsRef.current = [];
  }, []);

  return { chunks, isRunning, execute, retryFromFailed, reset };
};
